import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FulfilmentStatus } from "../../../core/dtos/FulfilmentStatus";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { Tooltip } from "@vapor/react-material";
import type { TFunction } from "i18next";
import { TaxSubmissionStatus } from "../../../core/dtos/TaxSubmissionStatus";

interface FulfilmentStatusTypographyProps {
    status: FulfilmentStatus;
    taxSubmissionType: string;
    taxSubmissionStatus: TaxSubmissionStatus;
    outcomeTypes: string[];
    revoked?: boolean;
}

const PAYMENT_OUTCOMES = ["I24A0", "I2401"];
const RECEIPT_OK_OUTCOMES = ["F24A0", "RLI12"];
const BASE_KEY = "components.fulfilment_status_chip";

const getStatusLabel = (
    t: TFunction,
    status: FulfilmentStatus,
    taxSubmissionType: string,
    taxSubmissionStatus: TaxSubmissionStatus,
    outcomeTypes: string[],
): string => {
    const hasPayment = PAYMENT_OUTCOMES.some((code) =>
        outcomeTypes.includes(code),
    );
    const isTaxSubmissionSent =
        taxSubmissionStatus === TaxSubmissionStatus.SENT ||
        taxSubmissionStatus === TaxSubmissionStatus.SENT_WITH_WARNINGS;

    if (isTaxSubmissionSent) {
        // SENT_WITH_WARNINGS but outcome not yet downloaded — still show pending
        if (status === FulfilmentStatus.Pending && outcomeTypes.length === 0)
            return t(`${BASE_KEY}.fulfilment_status.pending`);

        if (status === FulfilmentStatus.PaymentError)
            return t(`${BASE_KEY}.fulfilment_status.payment_error`);

        if (taxSubmissionType === "F24A0")
            return t(`${BASE_KEY}.fulfilment_status.done.f24_payed`);

        return t(
            hasPayment
                ? `${BASE_KEY}.fulfilment_status.done.payed`
                : `${BASE_KEY}.fulfilment_status.done`,
        );
    }

    const receiptOk =
        outcomeTypes.length === 1 &&
        RECEIPT_OK_OUTCOMES.includes(outcomeTypes[0]);

    if (status === FulfilmentStatus.Pending) {
        const statusKey = receiptOk
            ? "receipt_ok"
            : taxSubmissionStatus.toLowerCase();
        return t(`${BASE_KEY}.pending.tax_submission_status.${statusKey}`);
    }

    return t(
        `${BASE_KEY}.fulfilment_status.${status.toLowerCase() ?? "not_available"}`,
    );
};

export const FulfilmentStatusTypography: React.FC<
    FulfilmentStatusTypographyProps
> = ({
    status,
    taxSubmissionType,
    taxSubmissionStatus,
    outcomeTypes,
    revoked = false,
}): JSX.Element => {
    const textRef = useRef<HTMLElement>(null);
    const [truncated, setTruncated] = useState(false);

    const { t } = useTranslation();

    function handleMouseEnter() {
        const text = textRef.current;
        if (text) setTruncated(text.scrollWidth > text.clientWidth);
    }

    const label = getStatusLabel(
        t,
        status,
        taxSubmissionType,
        taxSubmissionStatus,
        outcomeTypes,
    );

    if (revoked)
        return (
            <Typography variant="body2">
                {t("components.fulfilment_status_chip.revoked")}
            </Typography>
        );

    return (
        <Tooltip title={truncated ? label : ""}>
            <Typography
                ref={textRef}
                variant="body2"
                noWrap
                onMouseEnter={handleMouseEnter}
            >
                {label}
            </Typography>
        </Tooltip>
    );
};
